import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../App';
import useLogger from '../hooks/useLogger';

// A real LGS result is flagged purely by naming convention (examNumber containing
// "GERÇEK") — there's no dedicated backend field for it since the data model has no DB.
function isRealExam(examNumber) {
  return typeof examNumber === 'string' && examNumber.toUpperCase().includes('GERÇEK');
}

function Dashboard({ onDataUpdate }) {
  const navigate = useNavigate();
  const logger = useLogger();
  const [students, setStudents] = useState([]);
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState('');
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    tcNo: '',
    schoolNo: '',
    name: '',
    class: ''
  });

  useEffect(() => {
    // Track page visit when coming to dashboard
    logger.trackPageVisit('dashboard');
    fetchExams();
  }, []);

  // Track when user navigates back to dashboard
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && window.location.pathname === '/') {
        logger.trackPageVisit('dashboard');
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => window.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (exams.length > 0) {
      fetchStudents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExam]);

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/exams`);
      // Real LGS pinned first in the dropdown, denemeler follow in chronological order.
      const real = response.data.filter(e => isRealExam(e.examNumber));
      const denemeler = response.data.filter(e => !isRealExam(e.examNumber));
      setExams([...real, ...denemeler]);
      if (denemeler.length > 0) {
        // Default to the latest deneme (full cohort) rather than the real exam
        // (which only ever has one student — Begüm — and would look empty/broken here).
        setSelectedExam(denemeler[denemeler.length - 1].examNumber);
      } else if (real.length > 0) {
        setSelectedExam(real[0].examNumber);
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  const handleExamChange = (examNumber) => {
    setSelectedExam(examNumber);
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const params = {
        examNumber: selectedExam,
        ...filters
      };
      
      const response = await axios.get(`${getApiUrl()}/students`, { params });
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // Filter tracking removed - not needed for logging
  };

  // Enter key tracking removed - not needed for logging

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchStudents();
  };

  const handleClearFilters = () => {
    setFilters({
      tcNo: '',
      schoolNo: '',
      name: '',
      class: ''
    });
    setTimeout(() => fetchStudents(), 0);
  };

  const handleStudentClick = (tcNo) => {
    const student = students.find(s => s.tcNo === tcNo);
    if (student) {
      logger.trackStudentInteraction(tcNo, student.name, 'student_clicked');
    }
    navigate(`/student-dashboard/${tcNo}`);
  };

  const getScoreColor = (score) => {
    if (score >= 450) return 'text-green-600 bg-green-100';
    if (score >= 400) return 'text-blue-600 bg-blue-100';
    if (score >= 350) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (exams.length === 0) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="text-6xl mb-4">📋</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Henüz Deneme Yüklenmedi
          </h2>
          <p className="text-gray-600 mb-6">
            Başlamak için deneme sonuçlarını içeren Excel dosyalarını yükleyin.
          </p>
          <button
            onClick={() => navigate('/upload')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-semibold"
          >
            Dosya Yükle
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900">Öğrenci Listesi</h2>
        <p className="mt-2 text-gray-600">
          Deneme sonuçlarını görüntüleyin ve öğrenci detaylarına erişin
        </p>
      </div>

      {/* Exam Selector */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Deneme Seçin:
        </label>
        <select
          value={selectedExam}
          onChange={(e) => handleExamChange(e.target.value)}
          className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {exams.map((exam, idx) => (
            <React.Fragment key={exam.examNumber}>
              <option value={exam.examNumber}>
                {isRealExam(exam.examNumber) ? `🏆 ${exam.examTitle}` : exam.examTitle} - {exam.studentCount} Öğrenci
              </option>
              {isRealExam(exam.examNumber) && idx + 1 < exams.length && !isRealExam(exams[idx + 1].examNumber) && (
                <option disabled>──────────</option>
              )}
            </React.Fragment>
          ))}
        </select>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtreler</h3>
        <form onSubmit={handleFilterSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              TC No
            </label>
            <input
              type="text"
              name="tcNo"
              value={filters.tcNo}
              onChange={handleFilterChange}
              placeholder="TC No ara..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Okul No
            </label>
            <input
              type="text"
              name="schoolNo"
              value={filters.schoolNo}
              onChange={handleFilterChange}
              placeholder="Okul No ara..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Öğrenci Adı
            </label>
            <input
              type="text"
              name="name"
              value={filters.name}
              onChange={handleFilterChange}
              placeholder="İsim ara..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Şube
            </label>
            <input
              type="text"
              name="class"
              value={filters.class}
              onChange={handleFilterChange}
              placeholder="Şube ara..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex items-end space-x-2">
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-semibold"
            >
              Filtrele
            </button>
            <button
              type="button"
              onClick={handleClearFilters}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
            >
              Temizle
            </button>
          </div>
        </form>
      </div>

      {/* Students Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      ) : students.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <p className="text-gray-600">Filtre kriterlerinize uygun öğrenci bulunamadı.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" style={{ tableLayout: 'auto' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '60px', width: '60px' }}>
                    Sıra
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider resize" style={{ minWidth: '120px' }}>
                    TC No
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider resize" style={{ minWidth: '90px' }}>
                    Okul No
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider resize" style={{ minWidth: '150px' }}>
                    Öğrenci Adı
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider resize" style={{ minWidth: '120px' }}>
                    Okul
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider resize" style={{ minWidth: '90px' }}>
                    Sınıf/Şube
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider resize" style={{ minWidth: '110px' }}>
                    Toplam Puan
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider resize" style={{ minWidth: '120px' }}>
                    Genel Sıralama
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider resize" style={{ minWidth: '110px' }}>
                    Toplam Net
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {students.map((student, index) => (
                  <tr
                    key={student.tcNo}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleStudentClick(student.tcNo)}
                  >
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 font-mono">
                      {student.tcNo}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                      {student.schoolNo}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {student.name}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-700">
                      {student.schoolName || '-'}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600">
                      {student.branch || student.class}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getScoreColor(student.totalScore)}`}>
                        {student.totalScore}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                      {isRealExam(selectedExam) ? '—' : student.generalRank}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                      {(student.totals.totalNet ?? 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Toplam <span className="font-semibold">{students.length}</span> öğrenci gösteriliyor
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;

